import React, { useEffect, useState } from 'react';
import { UnlockCelebration } from './UnlockCelebration';
import { useUpgradesStore } from '../stores/upgradesStore';

export function UnlockCelebrationManager() {
  const { unlockConfigurations, userUnlocks, celebratedUnlockKeys, markUnlocksCelebrated } = useUpgradesStore();
  const [celebrationQueue, setCelebrationQueue] = useState<string[]>([]);

  useEffect(() => {
    // Dedup against the PERSISTED celebrated set (by unlockKey) rather than
    // ephemeral component state — otherwise a remount (loading-spinner flicker)
    // or a re-login would re-show every existing unlock's celebration.
    const celebrated = new Set(celebratedUnlockKeys);

    /* ⚠️⚠️ AN UNLOCK WITH NO REQUIREMENTS WAS NEVER LOCKED, SO IT CANNOT BE AN
       EVENT. The Personal domain is `requirements: {}` — "Unlocked by default" —
       and on 2026-08-31 the first real signup was congratulated for unlocking
       it, in a modal over the onboarding welcome screen. The modal's own body
       text read "Unlocked by default", which is the celebration arguing against
       itself.

       ⚠️ The existing dedup could not catch this. It compares against the
       PERSISTED celebrated set, and a brand-new user's is empty — the `migrate`
       that seeds that set from current unlocks starts with
       `if (!persistedState) return persistedState`, so it covers upgrading users
       and never runs for a new one. Emptiness meant "nothing celebrated yet"
       when it actually meant "no baseline established".

       ⭐ Hence the rule is about the unlock, not about when we first saw it:
       a default unlock is never celebrated, for anyone, ever. That holds no
       matter what the persisted set contains — the same lesson as a zero
       baseline being hydration rather than an event. */
    const isDefaultUnlock = (unlockKey: string): boolean => {
      const config = unlockConfigurations.find(c => c.unlockKey === unlockKey);
      if (!config) return false;
      const req = config.requirements;
      return !req || Object.keys(req).length === 0;
    };

    const newUnlocks = userUnlocks.filter(
      unlock => !celebrated.has(unlock.unlockKey) && !isDefaultUnlock(unlock.unlockKey),
    );

    /* Default unlocks still enter the celebrated set, so this filter runs once
       per user rather than on every render for the rest of their life. */
    const defaults = userUnlocks
      .filter(u => !celebrated.has(u.unlockKey) && isDefaultUnlock(u.unlockKey))
      .map(u => u.unlockKey);
    if (defaults.length > 0) markUnlocksCelebrated(defaults);

    if (newUnlocks.length > 0) {
      setCelebrationQueue(prev => {
        const queued = new Set(prev);
        const toAdd = newUnlocks.map(u => u.id).filter(id => !queued.has(id));
        return toAdd.length > 0 ? [...prev, ...toAdd] : prev;
      });
      // Persist immediately so an at-most-once guarantee survives remounts.
      markUnlocksCelebrated(newUnlocks.map(u => u.unlockKey));
    }
  }, [userUnlocks, unlockConfigurations, celebratedUnlockKeys, markUnlocksCelebrated]);

  const handleClose = () => {
    setCelebrationQueue(prev => prev.slice(1));
  };

  if (celebrationQueue.length === 0) return null;

  const currentUnlockId = celebrationQueue[0];
  const currentUnlock = userUnlocks.find(u => u.id === currentUnlockId);
  const unlockConfig = currentUnlock
    ? unlockConfigurations.find(c => c.unlockKey === currentUnlock.unlockKey)
    : null;

  if (!currentUnlock || !unlockConfig) {
    handleClose();
    return null;
  }

  return (
    <UnlockCelebration
      unlockName={unlockConfig.unlockName}
      unlockDescription={unlockConfig.unlockDescription}
      unlockType={unlockConfig.unlockType}
      icon={unlockConfig.icon}
      color={unlockConfig.color}
      onClose={handleClose}
    />
  );
}
